import { PlaceOutlined } from '@mui/icons-material';
import { Box, Tooltip } from '@mui/material';
import { FC, KeyboardEvent, MouseEvent } from 'react';

import { SameLocationInfo, SameLocationTarget } from './sameLocationVM';

/**
 * ツールチップに並べる訪問の上限。全部並べると 1 地点に 10 台以上寄る日で
 * ツールチップが画面を覆うため、溢れは件数だけ伝える。
 */
export const SAME_LOCATION_TOOLTIP_LIMIT = 6;

export type SameLocationTagProps = {
  info: SameLocationInfo;
  /**
   * 行を押したときに呼ぶ。渡すのは回転行の rowId で、呼び出し側が
   * 「その行を選択して車両リストをそこまでスクロール」する。
   * 未指定なら行をボタンにしない（押しても何も起きない操作を誘わない）。
   */
  onSelectVehicle?: (rowId: string) => void;
  /**
   * いま車両リストに出ている回転行の id。ここに無い行は押せなくする。
   *
   * 押せてしまうと、車両リストが動かないまま（DOM が未登録なので `scrollToRow` は
   * 何もしない）見えない選択だけが残り、一括解除などの対象に入る。
   * 「未配車の車両のみ表示」では割当済みの相手が必ずリストから消えるので、
   * 通常操作で到達する。未指定なら全部押せる（表示中の集合を持たない呼び出し側向け）。
   */
  visibleVehicleRowIds?: ReadonlySet<string>;
};

/**
 * 1 台ぶんの行。車両と回転を 1 つの見出しにまとめ、地点名を右に置く。
 *
 * Why 1 行に収める: 車両名・ドライバー・回転・地点名を縦に積むと 2 台で 6 行になり、
 * どこまでが 1 台なのか読めない塊になる（最初の実装がそうだった）。
 * ドライバー名と会社名を落としたのはこのため。押せば車両リストがその行まで動くので、
 * 誰が乗っているかはそちらで読める。
 */
const TargetRow: FC<{
  target: SameLocationTarget;
  onSelect?: (rowId: string) => void;
  /** false なら押せない（車両フィルタで隠れている） */
  selectable: boolean;
}> = ({ target, onSelect, selectable }) => {
  const heading = `${target.vehicleNumber} / ${target.rotationNumber}回転目`;
  const clickable = !!onSelect && selectable;

  return (
    <Box
      // 押せない行は disabled ではなく aria-disabled にする。「その車両に割ついている
      // が、いま絞り込みで隠れていて飛べない」ことを支援技術にも伝えたく、要素ごと
      // 読み取り不能にしたいわけではないため（(非表示) ラベルもその一部）。
      // Why not tabIndex={-1} も足す: この行はキーボードから到達できない
      //（ツールチップの中身は portal に出るのでバッジからの Tab は入らない。
      // 下の Tooltip の Why と既知の制限を参照）ので、足しても効果が無い。
      component="button"
      type="button"
      {...(clickable ? {} : { 'aria-disabled': true })}
      // 伝播停止はここに書かない。境界は portal の最も外側 (slotProps.popper) に
      // 1 か所だけ置く。2 か所あるとどちらが効いているのか読めない
      onClick={() => { if (clickable) onSelect?.(target.rowId); }}
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 0.75,
        width: '100%',
        mt: 0.25,
        px: 0.5,
        mx: -0.5,
        py: '1px',
        borderRadius: '4px',
        textAlign: 'left',
        // button にすると UA 既定の枠と背景が付くので消す。色は Tooltip から継承させる
        border: 'none',
        bgcolor: 'transparent',
        color: 'inherit',
        font: 'inherit',
        cursor: clickable ? 'pointer' : 'default',
        '&:hover': clickable ? { bgcolor: 'hsl(var(--background) / 0.16)' } : undefined,
      }}
    >
      <Box component="span" sx={{ flexShrink: 0, fontWeight: 700, whiteSpace: 'nowrap' }}>
        {heading}
      </Box>
      {/* 押せない理由を出す。黙って押せないと「壊れている」と読まれる */}
      {onSelect && !selectable && (
        <Box component="span" sx={{ flexShrink: 0, fontWeight: 400, opacity: 0.6 }}>(非表示)</Box>
      )}
      {/* 省略記号で切らない。地点名は「どこが重複しているか」そのものなので、
          長ければ折り返す。見出しが nowrap なので折り返しても行の頭は揃う */}
      <Box component="span" sx={{ minWidth: 0, flex: 1, opacity: 0.85, wordBreak: 'break-word' }}>
        {target.storeName}
      </Box>
    </Box>
  );
};

/**
 * 未割当案件の「同一地点がすでに他車両へ割当済み」タグ (log_web_issues#318)。
 *
 * 見た目はサンプル (logposeTech/route-planner の UnassignedPanel) 準拠。
 * 配色を配車カードの警告バッジ「他車両での取扱あり」と同じ primary 系に揃えるのは、
 * 「同一地点が他車両にもある」という同じ意味の表示だと読み手に分からせるため。
 */
export const SameLocationTag: FC<SameLocationTagProps> = ({
  info, onSelectVehicle, visibleVehicleRowIds,
}) => {
  const { targets } = info;
  if (targets.length === 0) return null;

  const shown = targets.slice(0, SAME_LOCATION_TOOLTIP_LIMIT);
  const rest = targets.length - shown.length;

  return (
    <Tooltip
      placement="left"
      enterDelay={120}
      // describeChild を付けないと Tooltip がバッジに aria-labelledby を張り、
      // バッジの読み上げ名がツールチップ全文に置き換わる（UnassignedCardContent の
      // 「分割を戻す」Tooltip と同じ理由）。バッジ自身の文言を名前として残す
      describeChild
      slotProps={{
        /*
         * 伝播を止める境界はここ 1 か所だけ。ツールチップの中身は portal に出るが
         * React ツリー上は Tooltip の子なので、synthetic event は未割当カードまで
         * 伝播する。漏れると未割当案件の選択が切り替わり、カード型配車では
         * pointerDown から 3px 動かすだけで D&D が始まる。
         *
         * Why 行ごとではなく popper: 行に付けると見出し・「ほか N 件」・パネルの余白が
         * 漏れる（実際にそうなっていた）。popper は portal の最も外側で、
         * transition と tooltip 箱をまとめて包むのでこれ 1 つで中身すべてを守れる
         * （インストール済みの Tooltip.js の PopperSlot → TransitionSlot →
         * TooltipSlot の入れ子と、role="tooltip" が BasePopper.js:193 で
         * popper 側に付くことを確認）。
         *
         * MUI 自身が popper へ渡すのは interactiveWrapperListeners の
         * onMouseOver / onMouseLeave / onFocus だけなので、ここで上書きしない。
         *
         * preventDefault はしない。すると行ボタン自身の click が出なくなる。
         * keyDown も止めるのは、親カードが Enter/Space を無条件に preventDefault()
         * ＋案件選択へ変換するため。
         */
        popper: {
          onClick: (e: MouseEvent) => { e.stopPropagation(); },
          onPointerDown: (e: MouseEvent) => { e.stopPropagation(); },
          onKeyDown: (e: KeyboardEvent) => { e.stopPropagation(); },
        },
        tooltip: {
          sx: { maxWidth: 420, bgcolor: 'hsl(var(--foreground))' },
        },
      }}
      title={(
        <Box sx={{ fontSize: 12, lineHeight: 1.6, minWidth: 240 }}>
          <Box sx={{ fontWeight: 700, mb: 0.5 }}>同一地点がすでに割当済みです</Box>
          {shown.map((t) => (
            // 同一行が同一座標の別名称カードを持つので、行だけでは key にならない
            <TargetRow
              key={`${t.rowId}-${t.storeName}`}
              target={t}
              onSelect={onSelectVehicle}
              selectable={!visibleVehicleRowIds || visibleVehicleRowIds.has(t.rowId)}
            />
          ))}
          {rest > 0 && <Box sx={{ mt: 0.5, opacity: 0.6 }}>{`ほか ${rest} 件`}</Box>}
        </Box>
      )}
    >
      <Box
        // button にするのはフォーカスできるようにするため。span だとキーボードだけでは
        // ツールチップを開けず、どの車両に割ついているか読む手段が無くなる。
        // 同じカードに並ぶ未割当理由バッジ (NotAllocReasonPresenter) も同じ形。
        component="button"
        type="button"
        // 未割当カードは D&D の掴み手で、クリック・Enter/Space で選択も切り替わる。
        // タグは読むだけのものなので、どれも起こさせない
        onClick={(e: MouseEvent) => e.stopPropagation()}
        onPointerDown={(e: MouseEvent) => e.stopPropagation()}
        onKeyDown={(e: KeyboardEvent) => e.stopPropagation()}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 0.5,
          px: 0.75,
          py: '2px',
          borderRadius: '3px',
          // button の UA 既定フォントを継承へ戻したうえで大きさを指定する
          fontFamily: 'inherit',
          fontSize: 10,
          fontWeight: 600,
          lineHeight: 1.2,
          cursor: 'help',
          bgcolor: 'hsl(var(--primary) / 0.12)',
          color: 'hsl(var(--primary))',
          border: '1px solid hsl(var(--primary) / 0.35)',
        }}
      >
        <PlaceOutlined sx={{ fontSize: 12 }} />
        {`同一地点 割当済 (${targets.length})`}
      </Box>
    </Tooltip>
  );
};

SameLocationTag.displayName = 'SameLocationTag';
